import React from 'react'
import { StyleSheet, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { AccessType, useFloors, useWorkspaces, useZone } from 'zens-store'
import { Button, Text } from 'zens-storybook'

import { usePermissions } from '@dms/hook'

import { InfoTable, InfoTableProps } from '../info'
import { BuildingField } from '../building'

const styles = StyleSheet.create({
  container: {},
  location: {
    marginLeft: 0,
  },
  skeletonTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
})

interface Props extends InfoTableProps {
  companyId: number
  zoneId: number
  showFloors?: boolean
}
const ZoneInfo = (props: Props) => {
  const { zoneId, companyId, showFloors = false, style, ...otherProps } = props
  const zone = useZone({ companyId, zoneId })
  const { createdBy, updatedBy, createdAt, updatedAt, buildingId = 0 } = zone.data || {}
  const { t } = useTranslation()
  const workspaces = useWorkspaces({ companyId, filter: { zoneId: [zoneId] } }, { enabled: showFloors })
  const floors = useFloors({ companyId }, { enabled: showFloors })
  const floorIds = new Set(workspaces.data?.data.map((workspace) => workspace.floorId))
  const floorNames = floors.data
    ?.filter((floor) => floorIds.has(floor.id))
    .map((floor) => floor.name)
    .join(', ')

  const activity = {
    createdBy,
    createdAt,
    updatedBy,
    updatedAt,
  }

  const config = {
    fields: [
      {
        label: t('Name'),
        value: zone.data?.name,
      },
      {
        label: t('Description'),
        value: zone.data?.description,
      },
      {
        label: t('Located at'),
        value: <BuildingField value={buildingId} companyId={companyId} style={styles.location} />,
      },
      ...(showFloors ? [{ label: t('Floors'), value: floorNames || '—' }] : []),
    ],
  }

  return (
    <InfoTable
      companyId={companyId}
      title={t('Zone details')}
      style={[styles.container, style]}
      config={config}
      activity={activity}
      unfolded={false}
      {...otherProps}
    />
  )
}

interface SkeletonProps {
  companyId: number
}
const Skeleton = (props: SkeletonProps) => {
  const { companyId } = props
  const { t } = useTranslation()
  const { hasPermission } = usePermissions(companyId)
  return (
    <InfoTable.Skeleton
      title={
        <View style={styles.skeletonTitle}>
          <Text level={4}>{t('Zone details')}</Text>
          {hasPermission(AccessType.Update, 'workspace') && <Button.Skeleton />}
        </View>
      }
      hasActivity
    />
  )
}
ZoneInfo.Skeleton = Skeleton

export default ZoneInfo
